import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterOutlet, Event as RouterEvent, NavigationEnd } from '@angular/router';
import { NavbarComponent } from './core/layout/navbar/navbar.component';
import { WSDepartureService } from './shared/services/ws-departure-service/implementations/ws-departure.service';
import { Observable, Subscription } from 'rxjs';
import { SoundService } from './shared/services/sound-service/sound.service';
import { useGeographic } from 'ol/proj';
import { Bell, CheckCircle2, CircleX, LucideAngularModule, X } from 'lucide-angular';
import { LucideIconData } from 'lucide-angular/icons/types';
import {
  NotificationsService,
  ShiftNotificationType,
} from './shared/services/notifications-service/notifications.service';
import { ErrorNotificationService } from './shared/services/error-notification-service/error-notification.service';
import { ChunkLoadErrorRecoveryService } from './core/services/chunk-load-error-recovery/chunk-load-error-recovery.service';

@Component({
  selector: 'app-root',
  standalone: true,
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [RouterOutlet, NavbarComponent, LucideAngularModule],
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly wsService = inject(WSDepartureService);
  private readonly soundService = inject(SoundService);
  private readonly router = inject(Router);
  private readonly notificationsService = inject(NotificationsService);
  private readonly errorNotificationService = inject(ErrorNotificationService);
  private readonly chunkLoadErrorRecoveryService = inject(ChunkLoadErrorRecoveryService);
  private lastConnectionState = false;
  protected readonly window = window;
  private readonly wsSubscription = new Subscription();
  private readonly soundSubscription = new Subscription();
  private readonly routerSubscription = new Subscription();
  private readonly isSoundActive$: Observable<boolean> = this.soundService.isSoundActive$;
  protected readonly bellIcon: LucideIconData = Bell;
  protected readonly checkCircleIcon: LucideIconData = CheckCircle2;
  protected readonly crossIcon: LucideIconData = X;
  protected readonly circleX: LucideIconData = CircleX;
  protected readonly shiftNotifications = this.notificationsService.shiftNotifications;
  protected readonly errorMessage = this.errorNotificationService.errorMessage;
  protected readonly isErrorClosing = this.errorNotificationService.isErrorClosing;

  ngOnInit(): void {
    this.chunkLoadErrorRecoveryService.startMonitoringChunkLoadErrors();

    this.wsSubscription.add(
      this.wsService.canConnectToCentrifugeController.subscribe(
        (connectionState: boolean): void => {
          if (connectionState && !this.lastConnectionState) {
            this.wsService.initCentrifuge();
          }
          this.lastConnectionState = connectionState;
        },
      ),
    );

    this.soundSubscription.add(
      this.isSoundActive$.subscribe((soundState: boolean): void => {
        const audioElement: HTMLAudioElement = document.getElementsByTagName('audio')[0];
        if (soundState && audioElement) {
          audioElement.play().then();
        }
      }),
    );

    this.soundSubscription.add(
      this.notificationsService.isShiftAlertSoundActive.subscribe((soundState: boolean): void => {
        const audioElement: HTMLAudioElement = document.getElementsByTagName('audio')[1];
        if (soundState && audioElement) {
          audioElement.play().then();
        }
      }),
    );

    useGeographic();

    setTimeout(() => window.HSStaticMethods?.autoInit?.(), 0);

    this.routerSubscription.add(
      this.router.events.subscribe((event: RouterEvent): void => {
        if (event instanceof NavigationEnd) {
          setTimeout(() => window.HSStaticMethods.autoInit(), 100);
        }
      }),
    );
  }

  protected goToTeamShiftChangesHistoryPage(): void {
    this.router.navigate(['/team-shift-changes-history']).then();
  }

  protected closeShiftNotification(type: ShiftNotificationType): void {
    this.notificationsService.closeNotification(type);
  }

  protected closeErrorNotification(): void {
    this.errorNotificationService.closeError();
  }

  protected logout(): void {
    localStorage.removeItem('rsToken');
    this.wsService.canConnectToCentrifugeController.next(false);
    this.wsService.unsubscribeCentrifuge();

    this.router.navigate(['/auth']);
  }

  ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
    this.wsService.unsubscribeCentrifuge();
    this.wsSubscription.unsubscribe();
    this.soundSubscription.unsubscribe();
  }
}
